import React, { useState, useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import { Overlay, Popover } from 'react-bootstrap'
import styled from '@emotion/styled'

import { useCapabilitiesStore } from '../../stores/capabilitiesStore'
import { ReactComponent as LockIcon } from '../../assets/svgs/lock.svg'

const LockBadge = styled.span({
  position: 'fixed',
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: '#1a1a2e',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 100,
  boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
  '& svg': { width: 11, height: 11 },
  '& svg path, & svg line, & svg rect, & svg circle': { stroke: '#fff', fill: 'none', strokeWidth: 2 },
})

function SubscriptionLockBadge({ targetEl }) {
  const [show, setShow] = useState(false)
  const badgeRef = useRef(null)

  const getPos = useCallback(() => {
    if (!targetEl) return { top: 0, left: 0 }
    const btn = targetEl.querySelector('button, a, [role="button"]') || targetEl
    const r = btn.getBoundingClientRect()
    return { top: r.top - 4, left: r.right - 16 }
  }, [targetEl])

  const [pos, setPos] = useState(() => getPos())

  useEffect(() => {
    const update = () => setPos(getPos())
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    const obs = new MutationObserver(update)
    if (targetEl) obs.observe(targetEl, { attributes: true, childList: true, subtree: true })
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
      obs.disconnect()
    }
  }, [getPos, targetEl])

  return createPortal(
    <>
      <LockBadge
        ref={badgeRef}
        style={{ top: pos.top, left: pos.left }}
        onClick={() => setShow(!show)}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        <LockIcon />
      </LockBadge>
      <Overlay target={badgeRef.current} show={show} placement="top">
        {(props) => (
          <Popover {...props}>
            <Popover.Body style={{ fontSize: 12, maxWidth: 220 }}>
              This feature is not available on your current plan or has been disabled by your account administrator.
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    </>,
    document.body,
  )
}

const DEFAULT_FALLBACK = null

export function CapabilityGate({
  permission,
  permissions,
  feature,
  requireAll = false,
  fallback = DEFAULT_FALLBACK,
  subscriptionBadge = false,
  children,
}) {
  const hasPermission = useCapabilitiesStore((state) => state.hasPermission)
  const hasAnyPermission = useCapabilitiesStore((state) => state.hasAnyPermission)
  const isEnabled = useCapabilitiesStore((state) => state.isEnabled)
  const isFeatureDisabled = useCapabilitiesStore((state) => state.isFeatureDisabled)
  const caps = useCapabilitiesStore((state) => state.capabilities)
  const scopeRef = useRef(null)
  const [anchor, setAnchor] = useState(null)

  // dim the first button after render
  useEffect(() => {
    const el = scopeRef.current
    if (!el) return
    const btn = el.querySelector('button:not([data-cap-ignore])')
    if (!btn) return
    const orig = { disabled: btn.disabled, opacity: btn.style.opacity, pointerEvents: btn.style.pointerEvents }
    btn.disabled = true
    btn.style.opacity = '0.5'
    btn.style.pointerEvents = 'none'
    btn.style.cursor = 'not-allowed'
    return () => {
      btn.disabled = orig.disabled
      btn.style.opacity = orig.opacity
      btn.style.pointerEvents = orig.pointerEvents
      btn.style.cursor = ''
    }
  })

  useEffect(() => {
    if (scopeRef.current) {
      setAnchor(scopeRef.current.firstElementChild || scopeRef.current)
    }
  }, [])

  const hasAccess = React.useMemo(() => {
    const checks = []
    if (permission) {
      checks.push(hasPermission(permission))
      if (checks[checks.length - 1]) checks.push(isEnabled(permission))
    }
    if (permissions?.length) checks.push(hasAnyPermission(permissions))
    if (feature) checks.push(isEnabled(feature))
    if (checks.length === 0) return true
    return requireAll ? checks.every(Boolean) : checks.some(Boolean)
  }, [caps, permission, permissions, feature, requireAll, hasPermission, hasAnyPermission, isEnabled])

  if (!hasAccess) {
    if (subscriptionBadge && permission && isFeatureDisabled(permission)) {
      return (
        <>
          <span ref={scopeRef} style={{ display: 'contents' }}>
            {children}
          </span>
          {anchor && <SubscriptionLockBadge targetEl={anchor} />}
        </>
      )
    }
    return <>{fallback}</>
  }
  return <>{children}</>
}

CapabilityGate.propTypes = {
  permission: PropTypes.string,
  permissions: PropTypes.arrayOf(PropTypes.string),
  feature: PropTypes.string,
  requireAll: PropTypes.bool,
  fallback: PropTypes.node,
  subscriptionBadge: PropTypes.bool,
  children: PropTypes.node.isRequired,
}

export function SkipAuthorizationOnCompanyCreate({ isCompanyCreate, permission, children }) {
  if (isCompanyCreate) return <>{children}</>
  return <CapabilityGate permission={permission}>{children}</CapabilityGate>
}

SkipAuthorizationOnCompanyCreate.propTypes = {
  isCompanyCreate: PropTypes.bool.isRequired,
  permission: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
}

export default CapabilityGate
